// components/AdsManager.js
import LogoutButton from './LogoutButton';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Plus, Edit2, Trash2, Save, X, Eye, EyeOff, MoveUp, MoveDown,
    AlertCircle, Image as ImageIcon, StepBack
} from 'lucide-react';
import { fetchAds, saveAdsToAPI, checkAPIHealth } from '../data/adsData';
import ImageUpload from './ImageUpload';
import ENV from '../config/env';

const AdsManager = () => {
    const [ads, setAds] = useState([]);
    const [editingAd, setEditingAd] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [apiStatus, setApiStatus] = useState('checking');
    const [formData, setFormData] = useState({
        id: '',
        title: '',
        description: '',
        imageUrl: null,
        imageFile: null,
        cta: '',
        ctaLink: '',
        bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        active: true,
        order: 1
    });

    // Check API health and load ads
    useEffect(() => {
        checkAPIHealthAndLoadAds();
    }, []);

    // Clean up when form closes
    useEffect(() => {
        if (!showForm && formData.imageFile) {
            // Clean up any pending file uploads
            setFormData(prev => ({ ...prev, imageFile: null }));
        }
    }, [showForm]);

    const checkAPIHealthAndLoadAds = async () => {
        setLoading(true);
        try {
            const isHealthy = await checkAPIHealth();
            if (isHealthy) {
                setApiStatus('online');
                await loadAds();
            } else {
                setApiStatus('offline');
                setError('No se puede conectar al servidor. Verifica que el backend esté corriendo.');
            }
        } catch (err) {
            setApiStatus('offline');
            setError('Error de conexión con el servidor');
        } finally {
            setLoading(false);
        }
    };

    const loadAds = async () => {
        try {
            const loadedAds = await fetchAds();
            setAds(loadedAds.sort((a, b) => a.order - b.order));
            setError(null);
        } catch (err) {
            setError('Error al cargar los anuncios');
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageSelected = (file) => {
        setFormData(prev => ({
            ...prev,
            imageFile: file,
            imageUrl: null // Clear any existing URL since we have a new file
        }));
    };

    const handleImageRemoved = (isServerImage = false) => {
        setFormData(prev => ({
            ...prev,
            imageFile: null,
            imageUrl: isServerImage ? null : prev.imageUrl
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            let imageUrl = formData.imageUrl;
            let oldImageUrl = null;

            // If editing an ad, store the old image URL for potential cleanup
            if (editingAd) {
                oldImageUrl = editingAd.imageUrl;
            }

            // Upload image first if there's a new file
            if (formData.imageFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', formData.imageFile);

                const uploadResponse = await fetch(`${ENV.BACKEND_URL}/upload`, {
                    method: 'POST',
                    body: uploadFormData
                });

                const uploadData = await uploadResponse.json();

                if (uploadData.success) {
                    imageUrl = uploadData.imageUrl;

                    // If we're replacing an image and there was an old image, delete it
                    if (oldImageUrl && oldImageUrl !== imageUrl) {
                        const oldFilename = oldImageUrl.split('/').pop();
                        try {
                            await fetch(`${ENV.BACKEND_URL}/images/${oldFilename}`, {
                                method: 'DELETE'
                            });
                            console.log('Old image deleted:', oldFilename);
                        } catch (deleteError) {
                            console.error('Error deleting old image:', deleteError);
                            // Don't throw - continue with save even if old image delete fails
                        }
                    }
                } else {
                    throw new Error('Error al subir la imagen');
                }
            }

            let updatedAds;
            const adData = {
                id: formData.id,
                title: formData.title,
                description: formData.description,
                imageUrl: imageUrl,
                cta: formData.cta,
                ctaLink: formData.ctaLink,
                bgColor: formData.bgColor,
                active: formData.active,
                order: formData.order
            };

            if (editingAd) {
                updatedAds = ads.map(ad =>
                    ad.id === editingAd.id
                        ? { ...adData, id: editingAd.id }
                        : ad
                );
            } else {
                const newAd = {
                    ...adData,
                    id: Date.now()
                };
                updatedAds = [...ads, newAd];
            }

            updatedAds.sort((a, b) => a.order - b.order);

            const result = await saveAdsToAPI(updatedAds);

            if (result.success) {
                setAds(updatedAds);
                resetForm();
            } else {
                setError('Error al guardar los anuncios');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Error al guardar los anuncios');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (ad) => {
        setEditingAd(ad);
        setFormData({
            ...ad,
            imageFile: null
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este anuncio?')) {
            setSaving(true);
            try {
                // Find the ad to delete
                const adToDelete = ads.find(ad => ad.id === id);

                // First delete the image if it exists
                if (adToDelete && adToDelete.imageUrl) {
                    const filename = adToDelete.imageUrl.split('/').pop();
                    try {
                        await fetch(`${ENV.BACKEND_URL}/images/${filename}`, {
                            method: 'DELETE'
                        });
                        console.log('Image deleted:', filename);
                    } catch (deleteError) {
                        console.error('Error deleting image:', deleteError);
                        // Continue with ad deletion even if image delete fails
                    }
                }

                // Then update the ads list
                const updatedAds = ads.filter(ad => ad.id !== id);
                const result = await saveAdsToAPI(updatedAds);

                if (result.success) {
                    setAds(updatedAds);
                } else {
                    setError('Error al eliminar el anuncio');
                }
            } catch (err) {
                console.error('Error:', err);
                setError('Error al eliminar el anuncio');
            } finally {
                setSaving(false);
            }
        }
    };

    const toggleActive = async (id) => {
        setSaving(true);
        try {
            const updatedAds = ads.map(ad =>
                ad.id === id ? { ...ad, active: !ad.active } : ad
            );
            const result = await saveAdsToAPI(updatedAds);

            if (result.success) {
                setAds(updatedAds);
            } else {
                setError('Error al actualizar el estado');
            }
        } catch (err) {
            setError('Error al actualizar el estado');
        } finally {
            setSaving(false);
        }
    };

    const moveAd = async (index, direction) => {
        const newAds = [...ads];
        const newIndex = direction === 'up' ? index - 1 : index + 1;

        if (newIndex >= 0 && newIndex < newAds.length) {
            const tempOrder = newAds[index].order;
            newAds[index].order = newAds[newIndex].order;
            newAds[newIndex].order = tempOrder;

            [newAds[index], newAds[newIndex]] = [newAds[newIndex], newAds[index]];

            setSaving(true);
            try {
                const result = await saveAdsToAPI(newAds.sort((a, b) => a.order - b.order));

                if (result.success) {
                    setAds(newAds.sort((a, b) => a.order - b.order));
                } else {
                    setError('Error al reordenar los anuncios');
                }
            } catch (err) {
                setError('Error al reordenar los anuncios');
            } finally {
                setSaving(false);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            id: '',
            title: '',
            description: '',
            imageUrl: null,
            imageFile: null,
            cta: '',
            ctaLink: '',
            bgColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            active: true,
            order: ads.length > 0 ? Math.max(...ads.map(a => a.order)) + 1 : 1
        });
        setEditingAd(null);
        setShowForm(false);
    };

    const presetGradients = [
        { name: 'Morado', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
        { name: 'Rosa', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
        { name: 'Azul', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
        { name: 'Verde', value: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
        { name: 'Naranja', value: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
        { name: 'Azul Oscuro', value: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)' }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400">Cargando gestor de anuncios...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 md:p-6">
            <div className="max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <h1 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
                        Gestor de Anuncios
                    </h1>
                    <p className="text-gray-300">
                        Administra los anuncios del carrusel
                    </p>

                    {/* API Status Indicator */}
                    <div className={`mt-4 inline-block px-4 py-2 rounded-full ${apiStatus === 'online' ? 'bg-green-500/20 text-green-400' :
                        apiStatus === 'offline' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                        }`}>
                        {apiStatus === 'online' ? '✅ Servidor de anuncios conectado' :
                            apiStatus === 'offline' ? '❌ Servidor desconectado' :
                                '🔄 Verificando conexión...'}
                    </div>
                </motion.div>

                {error && (
                    <div className="mt-2 text-red-400 text-sm flex items-center justify-center gap-2">
                        <AlertCircle size={16} />
                        ⚠️ {error}
                    </div>
                )}

                <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                    <div></div>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setShowForm(true)}
                            disabled={apiStatus !== 'online'}
                            className={`bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${apiStatus !== 'online' ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                        >
                            <Plus size={20} />
                            Nuevo Anuncio
                        </button>
                        <button
                            onClick={() => window.history.back()}
                            className={`flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/30 text-white rounded-lg smooth-transition`}
                            title="Regresar"
                        >
                            <StepBack size={18} />
                            <span>Regresar</span>
                        </button>
                        <LogoutButton />
                    </div>
                </div>

                {/* Ads List */}
                <div className="space-y-4">
                    {ads.map((ad, index) => (
                        <motion.div
                            key={ad.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="bg-gray-800 rounded-xl p-4 border border-gray-700 hover:border-primary/50 transition-colors"
                            style={{ background: ad.bgColor }}
                        >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-start gap-4 flex-1 min-w-0">
                                    {ad.imageUrl ? (
                                        <img
                                            src={ad.imageUrl.startsWith('http') ? ad.imageUrl : `${ENV.FILE_SERVER_URL}${ad.imageUrl}`}
                                            alt={ad.title}
                                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `${ENV.FILE_SERVER_URL}/images/gallery/default.jpg`;
                                            }}
                                        />
                                    ) : (
                                        <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <ImageIcon size={24} className="text-gray-500" />
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-bold text-white text-lg mb-1 truncate">{ad.title}</h3>
                                        <p className="text-white/80 text-sm line-clamp-2 break-words">
                                            {ad.description}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                                    <span className={`px-2 py-1 rounded text-xs ${ad.active ? 'bg-green-600' : 'bg-red-600'}`}>
                                        {ad.active ? 'Activo' : 'Inactivo'}
                                    </span>

                                    <button
                                        onClick={() => toggleActive(ad.id)}
                                        disabled={saving || apiStatus !== 'online'}
                                        className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                                        title={ad.active ? 'Desactivar' : 'Activar'}
                                    >
                                        {ad.active ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>

                                    <button
                                        onClick={() => moveAd(index, 'up')}
                                        disabled={index === 0 || saving || apiStatus !== 'online'}
                                        className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                                        title="Mover arriba"
                                    >
                                        <MoveUp size={18} />
                                    </button>

                                    <button
                                        onClick={() => moveAd(index, 'down')}
                                        disabled={index === ads.length - 1 || saving || apiStatus !== 'online'}
                                        className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                                        title="Mover abajo"
                                    >
                                        <MoveDown size={18} />
                                    </button>

                                    <button
                                        onClick={() => handleEdit(ad)}
                                        disabled={apiStatus !== 'online'}
                                        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
                                        title="Editar"
                                    >
                                        <Edit2 size={18} />
                                    </button>

                                    <button
                                        onClick={() => handleDelete(ad.id)}
                                        disabled={saving || apiStatus !== 'online'}
                                        className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                                        title="Eliminar"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}

                    {ads.length === 0 && (
                        <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
                            <div className="text-gray-400 mb-4">
                                <AlertCircle size={48} className="mx-auto" />
                            </div>
                            <h3 className="text-xl font-bold mb-2">No hay anuncios</h3>
                            <p className="text-gray-400 mb-6">
                                Comienza creando tu primer anuncio
                            </p>
                            <button
                                onClick={() => setShowForm(true)}
                                disabled={apiStatus !== 'online'}
                                className="bg-primary hover:bg-primary/80 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto disabled:opacity-50"
                            >
                                <Plus size={20} />
                                Crear Primer Anuncio
                            </button>
                        </div>
                    )}
                </div>

                {/* Form Modal */}
                {showForm && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[1000]"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                if (window.confirm('¿Estás seguro de que quieres cancelar? Los cambios no guardados se perderán.')) {
                                    resetForm();
                                }
                            }
                        }}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-gray-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold">
                                    {editingAd ? 'Editar Anuncio' : 'Nuevo Anuncio'}
                                </h2>
                                <button
                                    onClick={resetForm}
                                    className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            Título *
                                        </label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            Botón CTA *
                                        </label>
                                        <input
                                            type="text"
                                            name="cta"
                                            value={formData.cta}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Descripción *
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 h-24 focus:ring-2 focus:ring-primary focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            Enlace del botón *
                                        </label>
                                        <select
                                            name="ctaLink"
                                            value={formData.ctaLink}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                            required
                                        >
                                            <option value="">Selecciona una opción</option>
                                            <option value="#nosotros">Nosotros</option>
                                            <option value="#servicios">Servicios</option>
                                            <option value="#productos">Productos</option>
                                            <option value="#LogoTool">LogoTool</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">
                                            Imagen
                                        </label>
                                        <ImageUpload
                                            currentImage={formData.imageUrl}
                                            onImageSelected={handleImageSelected}
                                            onImageRemoved={handleImageRemoved}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Fondo (Gradiente)
                                    </label>
                                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                                        {presetGradients.map((gradient) => (
                                            <button
                                                key={gradient.value}
                                                type="button"
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    bgColor: gradient.value
                                                }))}
                                                className="h-10 rounded-lg border-2 transition-colors"
                                                style={{
                                                    background: gradient.value,
                                                    borderColor: formData.bgColor === gradient.value ? '#fff' : 'transparent'
                                                }}
                                                title={gradient.name}
                                            />
                                        ))}
                                    </div>
                                    <input
                                        type="text"
                                        name="bgColor"
                                        value={formData.bgColor}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
                                    />
                                </div>

                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="active"
                                            checked={formData.active}
                                            onChange={handleInputChange}
                                            className="rounded bg-gray-700 border-gray-600 text-primary focus:ring-primary"
                                        />
                                        <span>Anuncio activo</span>
                                    </label>
                                </div>

                                <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-6 py-2 bg-primary hover:bg-primary/80 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors"
                                    >
                                        {saving ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                                <span>Guardando...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Save size={18} />
                                                {editingAd ? 'Actualizar' : 'Crear'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdsManager;